import axios from "axios"

function defaultSelectItems(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return (
    data?.users ??
    data?.items ??
    data?.data?.users ??
    data?.data?.items ??
    []
  );
}

function defaultItemKey(item) {
  if (!item || typeof item !== "object") return String(item);
  return String(item.id ?? item.user_id ?? item._id ?? JSON.stringify(item));
}

/**
 * Fetches all items from a backend endpoint that enforces a per-request cap (e.g. 200).
 *
 * Pagination modes:
 * - `mode: "page"` (default): uses `page` + `page_size`
 * - `mode: "skip"`: uses `skip` + `limit`
 *
 * It accumulates results until:
 * - a page returns no items, or
 * - a page returns fewer than `pageSize`, or
 * - the response stops producing new unique items (safety for backends that ignore pagination params).
 */
export async function fetchAllPaginatedItems({
  url,
  token,
  params = {},
  headers = {},
  mode = "page",
  pageParam = "page",
  pageSizeParam = "page_size",
  skipParam = "skip",
  limitParam = "limit",
  startSkip = 0,
  pageSize = 200,
  maxPages = 200,
  selectItems,
  getItemKey,
  dedupe = true,
}) {
  if (!url) throw new Error("fetchAllPaginatedItems: url is required");

  const resolvedSelectItems = typeof selectItems === "function" ? selectItems : defaultSelectItems;
  const resolvedGetItemKey = typeof getItemKey === "function" ? getItemKey : defaultItemKey;

  const allItems = [];
  const seen = new Set();

  for (let i = 0; i < maxPages; i += 1) {
    const paginationParams =
      mode === "skip"
        ? {
            // Backends typically expect `skip` to be a 0-based item offset.
            // With the default `startSkip = 0`, the first request is `skip=0`.
            [skipParam]: startSkip + i * pageSize,
            [limitParam]: pageSize,
          }
        : {
            // Backends typically expect `page` to be 1-based.
            [pageParam]: i + 1,
            [pageSizeParam]: pageSize,
          };

    const res = await axios.get(url, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      params: {
        ...params,
        ...paginationParams,
      },
    });

    if (i === 0) {
  console.log("[LIVECLASSES RAW] endpoint=" + url, {
    params: { ...params, ...paginationParams },
    payload: res?.data,
  });
}

    const pageItems = resolvedSelectItems(res?.data);
    const items = Array.isArray(pageItems) ? pageItems : [];

    if (items.length === 0) break;

    if (!dedupe) {
      allItems.push(...items);
      if (items.length < pageSize) break;
      continue;
    }

    let newCount = 0;
    for (const item of items) {
      const key = resolvedGetItemKey(item);
      if (!seen.has(key)) {
        seen.add(key);
        allItems.push(item);
        newCount += 1;
      }
    }

    // Safety: backend might ignore pagination params and keep returning the same first page.
    if (newCount === 0) break;

    if (items.length < pageSize) break;
  }

  return allItems;
}
